import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  ThemeProvider,
  Card,
  CardContent,
  CardHeader,
  Breadcrumbs,
  Input,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";
import { getCacheClients, getClient } from "../API/clientAPI";
import { Link as RouterLink } from "react-router-dom";
import { getJobOrdersByStatusAndCompetency } from "../API/jobOrderAPI";
import { s3AccomplishmentImageUploader, ACCOMPLISHMENT_BUCKET_NAME } from "../Promotions/config/aws-config-accomplishment-images";
import { generateAccomplishmentReport } from "../API/accomplishmentReportAPI";
import { generateAccomplishmentReportId } from '../../utils/idGenerator';
import imageCompression from "browser-image-compression";
import { createTheme } from "@mui/material/styles";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import Link from "@mui/material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import BaseWrapper from "../../components/shared/BaseWrapper";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function CreateTermiteAccomplishmentReport() {
  const [clientsCache, setClientsCache] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  //const [contactPerson, setContactPerson] = useState(""); // Contact person state
  const [selectedContact, setSelectedContact] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("");
  const [jobOrders, setJobOrders] = useState([]);
  const [finalTermiteStatus, setFinalTermiteStatus] = useState(""); // Final Termite Status state
  const [searchFilter, setSearchFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [s3Url, setS3Url] = useState(""); // State to hold the generated S3 URL
  const [s3UrlPpt, setS3UrlPpt] = useState(""); // State to hold the generated S3 URL
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [reportId, setReportId] = useState(generateAccomplishmentReportId());

  const navigate = useNavigate(); // Initialize navigate

  const clientFilter = createFilterOptions({
    matchFrom: "any",                     // allow substring matches, not just prefix
    stringify: (option) => option?.name || "",
  });


  const determineDimensions = (uploadedImages) => {
    switch (uploadedImages) {
      case 1:
        return { width: 920, height: 1142 };
      case 2:
        return { width: 700, height: 700 };
      case 3:
        return { width: 383, height: 419 };
      case 4:
        return { width: 232, height: 232 };
      default:
        return { width: 0, height: 0 }; // Return default dimensions if no valid number
    }
  };



  const resizeImage = (file, dimensions, caption = "") => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Maintain aspect ratio by adjusting width or height
          const aspectRatio = img.width / img.height;
          if (img.width > img.height) {
            canvas.width = dimensions.width;
            canvas.height = dimensions.width / aspectRatio;
          } else {
            canvas.height = dimensions.height;
            canvas.width = dimensions.height * aspectRatio;
          }

          // Draw the resized image
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Add caption if provided
          if (caption) {
            ctx.font = "20px Arial";
            ctx.fillStyle = "white";
            ctx.textAlign = "center";
            ctx.fillText(caption, canvas.width / 2, canvas.height - 20);
          }

          // Export the resized image with caption as a blob
          canvas.toBlob((blob) => {
            // Check the size of the resulting blob
            if (blob.size <= 2 * 1024 * 1024) {
              // If size is less than or equal to 2MB, resolve the promise
              resolve(new File([blob], file.name, { type: file.type }));
            } else {
              // If size exceeds 2MB, compress the image further
              const quality = Math.max(0.5, (2 * 1024 * 1024) / blob.size); // Calculate quality factor
              canvas.toBlob(
                (compressedBlob) => {
                  resolve(
                    new File([compressedBlob], file.name, { type: file.type })
                  );
                },
                file.type,
                quality
              );
            }
          }, file.type);
        };
        img.src = event.target.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (jobIndex, imageIndex, file) => {
    const jobOrder = jobOrders[jobIndex];
    const uploadedImages = jobOrder.images.filter((img) => img.file).length + 1;
    const dimensions = determineDimensions(uploadedImages);
    const caption = jobOrder.images[imageIndex]?.caption || "";

    const resizedFile = await resizeImage(file, dimensions, caption);

    setJobOrders((prevJobOrders) => {
      const updatedJobOrders = [...prevJobOrders];
      const jobOrder = { ...updatedJobOrders[jobIndex] };
      const images = [...jobOrder.images];
      images[imageIndex] = { ...images[imageIndex], file: resizedFile };
      jobOrder.images = images;
      updatedJobOrders[jobIndex] = jobOrder;
      return updatedJobOrders;
    });
  };

  const dropdownOptions = {
    termite_status: ["Active Termite", "No Active Termite"],
    appointment_type: ["I. Initial", "II. Update"],
    service_availed: [
      "Phase I. Exterra Colony Elimination",
      "Phase II. Comprehensive Termite",
    ],
    action_taken: [
      "AG Box monitoring/refilling",
      "Inspection and spraying",
      "Drilling and injection",
      "Inspection, demolition and spraying",
      "The inspection was conducted. Spraying was performed using chemical.",
      "The inspection was conducted. Spot spraying was performed on all wooden materials. The location was inspected, and a comprehensive termite control was recommended.",
      "The inspection was conducted. An AG box was installed with Exterra.",
      "The inspection was conducted. Active termites were found with fast feeding activity. Refilled with Exterra.",
      "The inspection was conducted. Cracks in the flooring were observed, and items were directly stored on the floor. The baits were partially consumed due to a blockage in the hole inside the AG box. The entry point was reopened, and the baits were replaced.",
      "The inspection was conducted. Refilled Exterra as the baits were fully consumed.",
      "The inspection was conducted. The baits were partially consumed. Refilled Exterra.",
      "The inspection was conducted. Only springtails were observed. Ready for pull-out on the next visit.",
      "The inspection was conducted. The AG box was pulled out, and chemical was sprayed.",
      "The inspection was conducted. Chemicals were drilled and injected into holes.",
      "The inspection was conducted. Chemical was sprayed on all wooden materials for preventive maintenance.",
      "The inspection was conducted. The entire surroundings were sprayed. The client requested the restoration of the IGS.",
    ],
    description: [
      "Colony Elimination: As termites continue to feed on the bait, the colony population declines over weeks to months until complete elimination is achieved. Removal of the AG Box: Once no termite activity is observed in the AG box, it is removed, and the treated area is reinspected to confirm successful elimination.",
      "An AG box is an integral part of an Integrated Pest Management (IPM) approach to termite control. While effective, it is essential to pair its use with preventive measures such as moisture control, sealing cracks, and ensuring no wooden debris is left near structures. Regular professional inspections further enhance long-term termite management. Regular Inspections: The AG box is checked periodically (every 2 weeks) to monitor bait consumption and termite activity. Additional bait is added as needed if the initial bait is consumed.",
      "Regular Inspections: The AG box is checked periodically (every 2 weeks) to monitor bait consumption and termite activity. Additional bait is added as needed if the initial bait is consumed.",
      "Sub-Slab Injection: Drilling holes in concrete slabs, each with a diameter of one half (1/2) inch, spaced a maximum of 1 meter apart on sides without barriers. This process creates a protective layer between subterranean termites in the soil and the structures.",
      "The inspection was done. Conducted drilling and injected holes with chemicals.",
      "The inspection was done. Active termites (macro in IG Warehouse). Conducted chemical spraying.",
      "The inspection was done. Mound active termites. Conducted demolition of mounds and sprayed chemicals.",
    ],
  };

  useEffect(() => {
    const fetchCacheClients = async () => {
      try {
        setClientsLoading(true);
        const clientsCache = await getCacheClients();
        setClientsCache(clientsCache);
      } catch (err) {
        setError(`Failed to fetch clients: ${err.message}`);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchCacheClients();
  }, []);

  const [contacts, setContacts] = useState([]);

  const handleSearchClient = (event, value) => {
    setSelectedClient(value || null);
  };

  const handleClientSelect = async (client) => {
    const clientId = client?.id;
    setAddresses([]);
    setContacts([]); // Clear previous contacts

    if (!clientId) return;

    try {
      setIsLoading(true);
      const clientDetails = await getClient(clientId);
      const clientAddresses = clientDetails?.addresses || [];
      const clientContacts = clientDetails?.contacts || [];
      setAddresses(clientAddresses);
      setContacts(clientContacts); // Set fetched contacts
      setIsLoading(false);
    } catch (err) {
      setError(`Failed to fetch addresses or contacts: ${err.message}`);
      setIsLoading(false);
    }
  };

  // Function to handle field changes for both dropdown and text box
  const handleFieldChange = (index, field, value, isDropdown = false) => {
    setJobOrders((prevJobOrders) => {
      const updatedJobOrders = [...prevJobOrders];
      const jobOrder = { ...updatedJobOrders[index] };

      if (isDropdown) {
        jobOrder[`selected_${field}`] = value; // Update the dropdown state
        jobOrder[field] = value; // Also sync initial text with dropdown
      } else {
        jobOrder[field] = value; // Update only the text box
      }

      updatedJobOrders[index] = jobOrder;
      return updatedJobOrders;
    });
  };

  const handleSearchButtonClick = async () => {
    if (!selectedClient?.id || !selectedAddress) {
      setError("Please select a client to proceed.");
      return;
    }

    try {
      setIsLoading(true);

      const startDate = searchFilter.startDate || null;
      const endDate = searchFilter.endDate || null;

      const jobOrderResults = await getJobOrdersByStatusAndCompetency(
        selectedClient.id,
        startDate,
        endDate,
        selectedAddress
      );

      const enrichedJobOrders = jobOrderResults?.data.map((jobOrder) => ({
        ...jobOrder,
        job_order: jobOrder.id,
        job_date: jobOrder.job_date,
        appointment_type: dropdownOptions.appointment_type[0],
        termite_status: dropdownOptions.termite_status[0],
        service_availed: dropdownOptions.service_availed[0],
        location: "",
        action_taken: dropdownOptions.action_taken[0],
        description: dropdownOptions.description[0],
        inspection_location: "",
        images: Array(4).fill({ file: null, caption: "" }), // Initialize 4 image slots
      }));

      setJobOrders(enrichedJobOrders || []);
      setIsLoading(false);
    } catch (err) {
      setError(`Failed to fetch job orders: ${err.message}`);
      setIsLoading(false);
    }
  };

  const handleCaptionChange = (jobIndex, imageIndex, caption) => {
    setJobOrders((prevJobOrders) => {
      const updatedJobOrders = [...prevJobOrders];
      const jobOrder = { ...updatedJobOrders[jobIndex] };
      const images = [...jobOrder.images];
      images[imageIndex] = { ...images[imageIndex], caption };
      jobOrder.images = images;
      updatedJobOrders[jobIndex] = jobOrder;
      return updatedJobOrders;
    });
  };

  const handleUploadImages = async (jobIndex) => {
    const jobOrder = jobOrders[jobIndex];
    const uploadedUrls = [];
    //console.log(jobOrder);

    try {
      for (let i = 0; i < jobOrder.images.length; i++) {
        const image = jobOrder.images[i];
        if (image.file) {
          const fileName = `${reportId}/${reportId}-${jobOrder.id}-image-${i}.jpg`;
          const params = {
            Bucket: ACCOMPLISHMENT_BUCKET_NAME,
            Key: fileName,
            Body: image.file,
            ContentType: image.file.type,
          };
          console.log("Uploading file with params:", params);

          const uploadResult = await s3AccomplishmentImageUploader
            .upload(params)
            .promise();
          console.log("Upload result:", uploadResult);

          uploadedUrls.push({
            url: uploadResult.Location,
            caption: image.caption,
          });
        }
      }

      // Save the uploaded URLs to the jobOrder
      const updatedJobOrders = [...jobOrders];
      updatedJobOrders[jobIndex].uploadedImages = uploadedUrls;
      setJobOrders(updatedJobOrders);
      alert("Images uploaded successfully!");
    } catch (err) {
      console.error("Image upload failed:", err);
      alert("Failed to upload images.");
    }
  };

  const [appointmentType, setAppointmentType] = useState("");
  const [recommendation, setRecommendation] = useState(
    `Termites are attracted to moisture. Ensure proper drainage around your property, fix leaky pipes, and eliminate any standing water around your facility/home.
    Do not stock raw materials and finished goods directly on floors as these termites are highly attracted to cellulose items. 
    Provide space for the areas that are not accessible for regular inspection. 
    Seal any cracks or openings in your facility's foundation, walls, and roof. Termites can enter through tiny cracks, so sealing these entry points can help prevent infestations.
    Remove dead trees, stumps, and wood debris from your yard. These provide a ready food source for termites and can attract them to your property.
    Ensure good ventilation in crawl spaces and attics to reduce humidity levels. Termites thrive in humid environments, so proper ventilation can help deter them.
    Keep all areas clean and dry.
    Please call our office if you see any termite activities/infestation immediately.`
  );

  const handleAppointmentTypeChange = (event) => {
    setAppointmentType(event.target.value);
  };

  const handleRecommendationChange = (event) => {
    setRecommendation(event.target.value);
  };

  function downloadJSON(filename, data) {
    const jsonBlob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(jsonBlob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${filename}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const handleSubmit = async () => {
    if (!selectedClient?.id || !selectedAddress) {
      setErrorMessage("Please select a client and address to proceed.");
      return;
    }
    if (!selectedContact) {
      setError("Contact Person is required.");
      return;
    }
    const clientName = selectedClient ? selectedClient.name : ""; // Get client name directly from selectedClient
    const selectedAddressDetails = addresses.find(
      (address) => address.id === selectedAddress
    ); // Find the selected address

    // Format the address string
    const formattedAddress = selectedAddressDetails
      ? `${selectedAddressDetails.house_unit_street}, ${selectedAddressDetails.barangay}, ${selectedAddressDetails.city} - ${selectedAddressDetails.zipcode}`
      : "";

    // Google Slides Payload
    const slidesPayload = {
      id: reportId,
      name: "Accomplishment Report",
      client_id: selectedClient.id,
      text_placeholders: {
        client_name: clientName,
        contact_person: selectedContact,
        address_formatted: formattedAddress,
        final_termite_status: finalTermiteStatus,
      },
      job_orders: jobOrders.map((job) => ({
        job_order_id: job.id,
        appointment_type: job.appointment_type,
        job_date: job.job_date,
        termite_status: job.termite_status,
        location: job.location,
        description: job.description,
        action_taken: job.action_taken,
        service_availed: job.service_availed,
        captions: job.uploadedImages?.map((img) => img.caption) || [],
        images: [
          {
            url: job.uploadedImages?.[0]?.url || "",
            placeholder_id: "p10_i2",
          },
          {
            url: job.uploadedImages?.[1]?.url || "",
            placeholder_id: "p10_i3",
          },
          {
            url: job.uploadedImages?.[2]?.url || "",
            placeholder_id: "p10_i7",
          },
          {
            url: job.uploadedImages?.[3]?.url || "",
            placeholder_id: "p10_i8",
          },
          {
            url: job.uploadedImages?.[4]?.url || "",
            placeholder_id: "p10",
          },
        ].filter((img) => img.url), // Exclude images with blank URLs
      })),
    };

    try {
      setIsLoading(true);

      // Immediately download JSON      
      //downloadJSON("job_order_parameters", slidesPayload);
      //downloadJSON("db_parameters", jobOrderParameters);

      const response = await generateAccomplishmentReport(slidesPayload);

      if (response) {
        setS3Url(`https://boaz-accomplishment-generated.s3.amazonaws.com/${reportId}/${reportId}_termite_accomplishment_report.pdf`); // Use backticks for template literals
        setS3UrlPpt(`https://boaz-accomplishment-generated.s3.amazonaws.com/${reportId}/${reportId}_termite_accomplishment_report.pptx`); // Use backticks for template literals
        setSuccessMessage("Accomplishment Report submitted successfuly!");
      }
    } catch (error) {
      console.error("Error submitting accomplishment report:", error);
      setErrorMessage(
        `Failed to submit Accomplishment Report: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (

    <BaseWrapper
      titleIcon={<NoteAddIcon />}
      title="Operations"
      subTitle="Create Accomplishment Report"
      subTitleRoute={"/create-accomplishment-reports"}
      // createButtonLabel="Create Service Report"
      // onClickCreateButton={() => navigate("/create-service-report")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >

      <Grid item xs={12}>
        <StyledHr />
      </Grid>{" "}
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={4}>
          <Autocomplete
            options={clientsCache}
            getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={(options, params) => clientFilter(options, params)}
            openOnFocus
            autoHighlight
            value={selectedClient} // Controlled component
            onChange={(event, newValue) => {
              handleSearchClient(event, newValue);
              handleClientSelect(newValue);
            }}
            loading={clientsLoading}
            noOptionsText="No clients found"
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <div style={{display:'flex',flexDirection:'column'}}>
                  <span>{option.name}</span>
                  <small style={{opacity:.7}}>ID: {option.id}</small>
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client Name"
                variant="outlined"
                fullWidth
                disabled={isLoading}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={12} lg={8}>
          <FormControl fullWidth>
            <InputLabel id="address-label">Address</InputLabel>
            <Select
              labelId="address-label"
              label="Address"
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
              disabled={isLoading || !selectedClient?.id}
            >
              <MenuItem value="">
                <em>Select Address</em>
              </MenuItem>
              {addresses.map((address) => (
                <MenuItem key={address.id} value={address.id}>
                  {`${address.house_unit_street}, ${address.barangay}, ${address.city} - ${address.zipcode}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12} lg={4}>
          <TextField
            label="Start Date"
            type="date"
            value={searchFilter.startDate}
            onChange={(e) =>
              setSearchFilter((prev) => ({
                ...prev,
                startDate: e.target.value,
              }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={12} lg={4}>
          <TextField
            label="End Date"
            type="date"
            value={searchFilter.endDate}
            onChange={(e) =>
              setSearchFilter((prev) => ({
                ...prev,
                endDate: e.target.value,
              }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={isLoading}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={12}
          lg={4}
          container
          justifyContent={{
            xs: "center",
            md: "center",
            lg: "flex-start",
          }}
          alignItems="center"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchButtonClick}
            disabled={isLoading}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <StyledHr />
      </Grid>{" "}
      <Box mt={4}>
        {jobOrders.length > 0 ? (
          <Grid container spacing={2}>
            {jobOrders.map((jobOrder, index) => (
              <Grid item xs={12} key={index}>
                <Card variant="outlined">
                  <CardContent sx={{ padding: "20px" }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12} lg={4}>
                        <TextField
                          label="Job Order"
                          value={jobOrder.id}
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={12} lg={4}>
                        <TextField
                          label="Date"
                          value={jobOrder.job_date}
                          disabled
                          fullWidth
                          type="date"
                        />
                      </Grid>
                      <Grid item xs={12} md={12} lg={4}>
                        <TextField
                          label="Appointment Type"
                          select
                          value={
                            jobOrder.appointment_type || "I. Initial"
                          }
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              "appointment_type",
                              e.target.value
                            )
                          }
                          fullWidth
                        >
                          {dropdownOptions.appointment_type.map(
                            (option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={12} lg={4}>
                        <FormControl fullWidth>
                          <InputLabel id="termite-status-label">
                            Termite Status
                          </InputLabel>
                          <Select
                            labelId="termite-status-label"
                            label="Termite Status"
                            value={jobOrder.termite_status}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "termite_status",
                                e.target.value
                              )
                            }
                            fullWidth
                          >
                            {dropdownOptions.termite_status.map(
                              (option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={12} lg={4}>
                        <FormControl fullWidth>
                          <InputLabel id="service-availed-label">
                            Service Availed
                          </InputLabel>

                          <Select
                            labelId="service-availed-label"
                            label="Service Availed"
                            value={jobOrder.service_availed}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "service_availed",
                                e.target.value
                              )
                            }
                            fullWidth
                          >
                            {dropdownOptions.service_availed.map(
                              (option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={12} lg={4}>
                        <TextField
                          label="Inspection Location"
                          value={jobOrder.location}
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              "location",
                              e.target.value
                            )
                          }
                          placeholder="Enter location"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={4}>
                        <FormControl fullWidth>
                          <InputLabel id={`action-taken-label-${index}`}>Action Taken</InputLabel>
                          <Select
                            labelId={`action-taken-label-${index}`}
                            value={jobOrder.selected_action_taken || ""}
                            onChange={(e) => handleFieldChange(index, "action_taken", e.target.value, true)}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  fontSize: "12px",
                                  maxHeight: "200px",
                                },
                              },
                            }}
                          >
                            {dropdownOptions.action_taken.map((option) => (
                              <MenuItem key={option} value={option} sx={{ fontSize: "12px", padding: "4px 8px" }}>

                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          value={jobOrder.action_taken || ""}
                          onChange={(e) => handleFieldChange(index, "action_taken", e.target.value)}
                          multiline
                          rows={6}
                          fullWidth
                          style={{ marginTop: "10px" }}
                          InputProps={{ style: { fontSize: "12px" } }}
                        />
                      </Grid>


                      <Grid item xs={12} md={12} lg={4}>
                        <FormControl fullWidth>
                          <InputLabel id={`description-label-${index}`}>Description</InputLabel>
                          <Select
                            labelId={`description-label-${index}`}
                            value={jobOrder.selected_description || ""}
                            onChange={(e) => handleFieldChange(index, "description", e.target.value, true)}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  fontSize: "12px",
                                  maxHeight: "200px",
                                },
                              },
                            }}
                          >
                            {dropdownOptions.description.map((option) => (
                              <MenuItem
                                key={option}
                                value={option}
                                sx={{
                                  fontSize: "12px",
                                  padding: "4px 8px",
                                }}
                              >
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          value={jobOrder.description || ""}
                          onChange={(e) => handleFieldChange(index, "description", e.target.value)}
                          multiline
                          rows={6}
                          fullWidth
                          style={{ marginTop: "10px" }}
                          InputProps={{ style: { fontSize: "12px" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={12} lg={4}>
                        <Typography variant="subtitle1" gutterBottom>
                          Images & Captions
                        </Typography>
                        {jobOrder.images.map((image, imageIndex) => (
                          <Box key={imageIndex} mb={2}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleImageChange(
                                  index,
                                  imageIndex,
                                  e.target.files[0]
                                )
                              }
                            />
                            <TextField
                              fullWidth
                              label={`Caption ${imageIndex + 1}`}
                              value={image.caption}
                              onChange={(e) =>
                                handleCaptionChange(
                                  index,
                                  imageIndex,
                                  e.target.value
                                )
                              }
                              size="small"
                            />
                          </Box>
                        ))}
                        <Grid
                          item
                          xs={12}
                          container
                          justifyContent="center"
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleUploadImages(index)}
                          >
                            Upload Images
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{ height: "100%" }}
          >
            <Typography>No job orders found.</Typography>
          </Grid>
        )}
      </Box>
      <Grid item xs={12}>
        <StyledHr />
      </Grid>{" "}
      {/* Multiline Text Box */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Recommendation"
            multiline
            rows={10}
            value={recommendation}
            onChange={handleRecommendationChange}
            fullWidth
            InputProps={{ sx: { fontSize: "1rem" } }}
            InputLabelProps={{ sx: { fontSize: "1rem" } }}
            disabled={isLoading}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Contact Person</InputLabel>
            <Select
              value={selectedContact}
              onChange={(e) => setSelectedContact(e.target.value)}
              disabled={contacts.length === 0}
            >
              <MenuItem value="">
                <em>Select Contact Person</em>
              </MenuItem>
              {contacts.map((contact) => (
                <MenuItem key={contact.id} value={`${contact.firstname} ${contact.lastname}`}>
                  {`${contact.firstname} ${contact.lastname}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Final Termite Status</InputLabel>
            <Select
              value={finalTermiteStatus}
              onChange={(e) => setFinalTermiteStatus(e.target.value)}
              disabled={isLoading}
            >
              <MenuItem value="">
                <em>Select Termite Status</em>
              </MenuItem>
              <MenuItem value="Active Termites">
                Active Termites
              </MenuItem>
              <MenuItem value="No Active Termites">
                No Active Termites
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12}>
          <Card variant="outlined" sx={{ padding: "20px", marginBottom: "20px" }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Generated Reports & Actions
                </Typography>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                {s3Url && (
                  <Grid item xs={12}>
                    <Box>
                      <Link
                        href={s3Url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: "none",
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Termite Accomplishment Report PDF Format
                        <OpenInNewIcon sx={{ marginLeft: 1, fontSize: "1rem" }} />
                      </Link>
                    </Box>
                  </Grid>
                )}
                {s3UrlPpt && (
                  <Grid item xs={12}>
                    <Box>
                      <Link
                        href={s3UrlPpt}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: "none",
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Termite Accomplishment Report PPT Format
                        <OpenInNewIcon sx={{ marginLeft: 1, fontSize: "1rem" }} />
                      </Link>
                    </Box>
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/manage-accomplishment-reports")}
                    sx={{
                      fontSize: "0.875rem",
                      color: "#FF4C4C",
                      borderColor: "#FF4C4C",
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                    disabled={isLoading}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>



      </Grid>
    </BaseWrapper>
    // <ThemeProvider theme={theme}>
    //   {/* Success Snackbar */}
    //   <Snackbar
    //     open={!!successMessage}
    //     autoHideDuration={3000}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setSuccessMessage("")}
    //   >
    //     <Alert
    //       severity="success"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {successMessage}
    //     </Alert>
    //   </Snackbar>

    //   {/* Loading Snackbar */}
    //   <Snackbar
    //     open={isLoading}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //   >
    //     <Alert
    //       severity="info"
    //       icon={<CircularProgress size={18} />}
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       Loading, please wait...
    //     </Alert>
    //   </Snackbar>

    //   {/* Error Snackbar */}
    //   <Snackbar
    //     open={!!errorMessage}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setErrorMessage("")}
    //   >
    //     <Alert
    //       severity="error"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {errorMessage}
    //     </Alert>
    //   </Snackbar>
    //   <Box sx={{ padding: { xs: "0px", md: "20px 30px", lg: "40px 60px" } }}>
    //     <Card
    //       variant="outlined"
    //       style={{
    //         marginTop: "40px",
    //         marginBottom: "40px",
    //         borderRadius: 0,
    //       }}
    //     >
    //       <CardHeader
    //         title={
    //           <Grid
    //             container
    //             alignItems="center"
    //             justifyContent="space-between"
    //           >
    //             <Grid item xs={12} md="auto">
    //               <Box display="flex" alignItems="center">
    //                 <NoteAddIcon
    //                   sx={{
    //                     fontSize: { xs: "30px", md: "40px" },
    //                     marginRight: "8px",
    //                   }}
    //                 />
    //                 <Typography
    //                   variant="h6"
    //                   sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
    //                 >
    //                   Operations
    //                 </Typography>
    //               </Box>
    //             </Grid>
    //             <Grid item xs={12} md="auto">
    //               <Breadcrumbs
    //                 aria-label="breadcrumb"
    //                 separator={<NavigateNextIcon fontSize="small" />}
    //                 sx={{ color: "white", marginTop: { xs: "8px", md: "0" } }}
    //               >
    //                 <Typography
    //                   sx={{
    //                     color: "#ffffff99",
    //                     fontSize: { xs: "0.75rem", md: "1rem" },
    //                   }}
    //                 >
    //                   Operations
    //                 </Typography>

    //                 <Typography
    //                   sx={{
    //                     color: "#ffffff",
    //                     textDecoration: "none",
    //                     fontSize: { xs: "0.75rem", md: "1rem" },
    //                   }}
    //                 >
    //                   Create Accomplishment Report
    //                 </Typography>
    //               </Breadcrumbs>
    //             </Grid>
    //           </Grid>
    //         }
    //         sx={{
    //           backgroundColor: "#1e5d45",
    //           color: "white",
    //           padding: { xs: "8px", md: "16px" },
    //         }}
    //       />
    //       <CardContent sx={{ padding: { xs: "10px", md: "20px" } }}>
    //         <Card
    //           variant="outlined"
    //           sx={{ padding: { xs: "10px", md: "20px" } }}
    //         >
    //           <CardHeader
    //             title={
    //               <Typography variant="h5" sx={{ fontWeight: "bold" }}>
    //                 Create Accomplishment Report
    //               </Typography>
    //             }
    //             sx={{ marginBottom: -2 }}
    //           />
    //           <Grid item xs={12}>
    //             <StyledHr />
    //           </Grid>{" "}
    //           <Grid container spacing={3}>
    //             <Grid item xs={12} md={12} lg={4}>
    //               <FormControl fullWidth>
    //                 <InputLabel id="client-name-label">Client Name</InputLabel>
    //                 <Select
    //                   labelId="client-name-label"
    //                   label="Client Name"
    //                   value={selectedClient}
    //                   onChange={(e) => handleClientSelect(e.target.value)}
    //                   disabled={isLoading}
    //                 >
    //                   <MenuItem value="">
    //                     <em>Select Client</em>
    //                   </MenuItem>
    //                   {clientsCache.map((client) => (
    //                     <MenuItem key={client.id} value={client.id}>
    //                       {client.name}
    //                     </MenuItem>
    //                   ))}
    //                 </Select>
    //               </FormControl>
    //             </Grid>

    //             <Grid item xs={12} md={12} lg={8}>
    //               <FormControl fullWidth>
    //                 <InputLabel id="address-label">Address</InputLabel>
    //                 <Select
    //                   labelId="address-label"
    //                   label="Address"
    //                   value={selectedAddress}
    //                   onChange={(e) => setSelectedAddress(e.target.value)}
    //                   disabled={isLoading || !selectedClient}
    //                 >
    //                   <MenuItem value="">
    //                     <em>Select Address</em>
    //                   </MenuItem>
    //                   {addresses.map((address) => (
    //                     <MenuItem key={address.id} value={address.id}>
    //                       {`${address.house_unit_street}, ${address.barangay}, ${address.city} - ${address.zipcode}`}
    //                     </MenuItem>
    //                   ))}
    //                 </Select>
    //               </FormControl>
    //             </Grid>

    //             <Grid item xs={12} md={12} lg={4}>
    //               <TextField
    //                 label="Start Date"
    //                 type="date"
    //                 value={searchFilter.startDate}
    //                 onChange={(e) =>
    //                   setSearchFilter((prev) => ({
    //                     ...prev,
    //                     startDate: e.target.value,
    //                   }))
    //                 }
    //                 fullWidth
    //                 InputLabelProps={{ shrink: true }}
    //                 disabled={isLoading}
    //               />
    //             </Grid>

    //             <Grid item xs={12} md={12} lg={4}>
    //               <TextField
    //                 label="End Date"
    //                 type="date"
    //                 value={searchFilter.endDate}
    //                 onChange={(e) =>
    //                   setSearchFilter((prev) => ({
    //                     ...prev,
    //                     endDate: e.target.value,
    //                   }))
    //                 }
    //                 fullWidth
    //                 InputLabelProps={{ shrink: true }}
    //                 disabled={isLoading}
    //               />
    //             </Grid>

    //             <Grid
    //               item
    //               xs={12}
    //               md={12}
    //               lg={4}
    //               container
    //               justifyContent={{
    //                 xs: "center",
    //                 md: "center",
    //                 lg: "flex-start",
    //               }}
    //               alignItems="center"
    //             >
    //               <Button
    //                 variant="contained"
    //                 color="primary"
    //                 onClick={handleSearchButtonClick}
    //                 disabled={isLoading}
    //               >
    //                 Search
    //               </Button>
    //             </Grid>
    //           </Grid>
    //           <Grid item xs={12}>
    //             <StyledHr />
    //           </Grid>{" "}
    //           <Box mt={4}>
    //             {jobOrders.length > 0 ? (
    //               <Grid container spacing={2}>
    //                 {jobOrders.map((jobOrder, index) => (
    //                   <Grid item xs={12} key={index}>
    //                     <Card variant="outlined">
    //                       <CardContent sx={{ padding: "20px" }}>
    //                         <Grid container spacing={3}>
    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <TextField
    //                               label="Job Order"
    //                               value={jobOrder.id}
    //                               disabled
    //                               fullWidth
    //                             />
    //                           </Grid>
    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <TextField
    //                               label="Date"
    //                               value={jobOrder.job_date}
    //                               disabled
    //                               fullWidth
    //                               type="date"
    //                             />
    //                           </Grid>
    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <TextField
    //                               label="Appointment Type"
    //                               select
    //                               value={
    //                                 jobOrder.appointment_type || "I. Initial"
    //                               }
    //                               onChange={(e) =>
    //                                 handleFieldChange(
    //                                   index,
    //                                   "appointment_type",
    //                                   e.target.value
    //                                 )
    //                               }
    //                               fullWidth
    //                             >
    //                               {dropdownOptions.appointment_type.map(
    //                                 (option) => (
    //                                   <MenuItem key={option} value={option}>
    //                                     {option}
    //                                   </MenuItem>
    //                                 )
    //                               )}
    //                             </TextField>
    //                           </Grid>
    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <FormControl fullWidth>
    //                               <InputLabel id="termite-status-label">
    //                                 Termite Status
    //                               </InputLabel>
    //                               <Select
    //                                 labelId="termite-status-label"
    //                                 label="Termite Status"
    //                                 value={jobOrder.termite_status}
    //                                 onChange={(e) =>
    //                                   handleFieldChange(
    //                                     index,
    //                                     "termite_status",
    //                                     e.target.value
    //                                   )
    //                                 }
    //                                 fullWidth
    //                               >
    //                                 {dropdownOptions.termite_status.map(
    //                                   (option) => (
    //                                     <MenuItem key={option} value={option}>
    //                                       {option}
    //                                     </MenuItem>
    //                                   )
    //                                 )}
    //                               </Select>
    //                             </FormControl>
    //                           </Grid>
    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <FormControl fullWidth>
    //                               <InputLabel id="service-availed-label">
    //                                 Service Availed
    //                               </InputLabel>

    //                               <Select
    //                                 labelId="service-availed-label"
    //                                 label="Service Availed"
    //                                 value={jobOrder.service_availed}
    //                                 onChange={(e) =>
    //                                   handleFieldChange(
    //                                     index,
    //                                     "service_availed",
    //                                     e.target.value
    //                                   )
    //                                 }
    //                                 fullWidth
    //                               >
    //                                 {dropdownOptions.service_availed.map(
    //                                   (option) => (
    //                                     <MenuItem key={option} value={option}>
    //                                       {option}
    //                                     </MenuItem>
    //                                   )
    //                                 )}
    //                               </Select>
    //                             </FormControl>
    //                           </Grid>
    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <TextField
    //                               label="Inspection Location"
    //                               value={jobOrder.location}
    //                               onChange={(e) =>
    //                                 handleFieldChange(
    //                                   index,
    //                                   "location",
    //                                   e.target.value
    //                                 )
    //                               }
    //                               placeholder="Enter location"
    //                               fullWidth
    //                             />
    //                           </Grid>

    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <FormControl fullWidth>
    //                               <InputLabel id={`action-taken-label-${index}`}>Action Taken</InputLabel>
    //                               <Select
    //                                 labelId={`action-taken-label-${index}`}
    //                                 value={jobOrder.selected_action_taken || ""}
    //                                 onChange={(e) => handleFieldChange(index, "action_taken", e.target.value, true)}
    //                                 MenuProps={{
    //                                   PaperProps: {
    //                                     style: {
    //                                       fontSize: "12px",
    //                                       maxHeight: "200px",
    //                                     },
    //                                   },
    //                                 }}
    //                               >
    //                                 {dropdownOptions.action_taken.map((option) => (
    //                                   <MenuItem key={option} value={option} sx={{ fontSize: "12px", padding: "4px 8px" }}>

    //                                     {option}
    //                                   </MenuItem>
    //                                 ))}
    //                               </Select>
    //                             </FormControl>

    //                             <TextField
    //                               value={jobOrder.action_taken || ""}
    //                               onChange={(e) => handleFieldChange(index, "action_taken", e.target.value)}
    //                               multiline
    //                               rows={6}
    //                               fullWidth
    //                               style={{ marginTop: "10px" }}
    //                               InputProps={{ style: { fontSize: "12px" } }}
    //                             />
    //                           </Grid>


    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <FormControl fullWidth>
    //                               <InputLabel id={`description-label-${index}`}>Description</InputLabel>
    //                               <Select
    //                                 labelId={`description-label-${index}`}
    //                                 value={jobOrder.selected_description || ""}
    //                                 onChange={(e) => handleFieldChange(index, "description", e.target.value, true)}
    //                                 MenuProps={{
    //                                   PaperProps: {
    //                                     style: {
    //                                       fontSize: "12px",
    //                                       maxHeight: "200px",
    //                                     },
    //                                   },
    //                                 }}
    //                               >
    //                                 {dropdownOptions.description.map((option) => (
    //                                   <MenuItem
    //                                     key={option}
    //                                     value={option}
    //                                     sx={{
    //                                       fontSize: "12px",
    //                                       padding: "4px 8px",
    //                                     }}
    //                                   >
    //                                     {option}
    //                                   </MenuItem>
    //                                 ))}
    //                               </Select>
    //                             </FormControl>

    //                             <TextField
    //                               value={jobOrder.description || ""}
    //                               onChange={(e) => handleFieldChange(index, "description", e.target.value)}
    //                               multiline
    //                               rows={6}
    //                               fullWidth
    //                               style={{ marginTop: "10px" }}
    //                               InputProps={{ style: { fontSize: "12px" } }}
    //                             />
    //                           </Grid>
    //                           <Grid item xs={12} md={12} lg={4}>
    //                             <Typography variant="subtitle1" gutterBottom>
    //                               Images & Captions
    //                             </Typography>
    //                             {jobOrder.images.map((image, imageIndex) => (
    //                               <Box key={imageIndex} mb={2}>
    //                                 <input
    //                                   type="file"
    //                                   accept="image/*"
    //                                   onChange={(e) =>
    //                                     handleImageChange(
    //                                       index,
    //                                       imageIndex,
    //                                       e.target.files[0]
    //                                     )
    //                                   }
    //                                 />
    //                                 <TextField
    //                                   fullWidth
    //                                   label={`Caption ${imageIndex + 1}`}
    //                                   value={image.caption}
    //                                   onChange={(e) =>
    //                                     handleCaptionChange(
    //                                       index,
    //                                       imageIndex,
    //                                       e.target.value
    //                                     )
    //                                   }
    //                                   size="small"
    //                                 />
    //                               </Box>
    //                             ))}
    //                             <Grid
    //                               item
    //                               xs={12}
    //                               container
    //                               justifyContent="center"
    //                             >
    //                               <Button
    //                                 variant="contained"
    //                                 color="primary"
    //                                 onClick={() => handleUploadImages(index)}
    //                               >
    //                                 Upload Images
    //                               </Button>
    //                             </Grid>
    //                           </Grid>
    //                         </Grid>
    //                       </CardContent>
    //                     </Card>
    //                   </Grid>
    //                 ))}
    //               </Grid>
    //             ) : (
    //               <Grid
    //                 container
    //                 justifyContent="center"
    //                 alignItems="center"
    //                 style={{ height: "100%" }}
    //               >
    //                 <Typography>No job orders found.</Typography>
    //               </Grid>
    //             )}
    //           </Box>
    //           <Grid item xs={12}>
    //             <StyledHr />
    //           </Grid>{" "}
    //           {/* Multiline Text Box */}
    //           <Grid container spacing={3}>
    //             <Grid item xs={12}>
    //               <TextField
    //                 label="Recommendation"
    //                 multiline
    //                 rows={10}
    //                 value={recommendation}
    //                 onChange={handleRecommendationChange}
    //                 fullWidth
    //                 InputProps={{ sx: { fontSize: "1rem" } }}
    //                 InputLabelProps={{ sx: { fontSize: "1rem" } }}
    //                 disabled={isLoading}
    //               />
    //             </Grid>
    //             <Grid item xs={12}>
    //             <FormControl fullWidth>
    //               <InputLabel>Contact Person</InputLabel>
    //                 <Select
    //                     value={selectedContact}
    //                     onChange={(e) => setSelectedContact(e.target.value)}
    //                     disabled={contacts.length === 0}
    //                 >
    //                     <MenuItem value="">
    //                         <em>Select Contact Person</em>
    //                     </MenuItem>
    //                     {contacts.map((contact) => (
    //                         <MenuItem key={contact.id} value={`${contact.firstname} ${contact.lastname}`}>
    //                             {`${contact.firstname} ${contact.lastname}`}
    //                         </MenuItem>
    //                     ))}
    //                 </Select>
    //             </FormControl>
    //             </Grid>
    //             <Grid item xs={12}>
    //               <FormControl fullWidth>
    //                 <InputLabel>Final Termite Status</InputLabel>
    //                 <Select
    //                   value={finalTermiteStatus}
    //                   onChange={(e) => setFinalTermiteStatus(e.target.value)}
    //                   disabled={isLoading}
    //                 >
    //                   <MenuItem value="">
    //                     <em>Select Termite Status</em>
    //                   </MenuItem>
    //                   <MenuItem value="Active Termites">
    //                     Active Termites
    //                   </MenuItem>
    //                   <MenuItem value="No Active Termites">
    //                     No Active Termites
    //                   </MenuItem>
    //                 </Select>
    //               </FormControl>
    //             </Grid>

    //             <Grid item xs={12} md={12}>
    //               <Card variant="outlined" sx={{ padding: "20px", marginBottom: "20px" }}>
    //                 <CardHeader
    //                   title={
    //                     <Typography
    //                       variant="h6"
    //                       sx={{
    //                         fontWeight: "bold",
    //                         textTransform: "uppercase",
    //                         fontSize: "1rem",
    //                       }}
    //                     >
    //                       Generated Reports & Actions
    //                     </Typography>
    //                   }
    //                 />
    //                 <CardContent>
    //                   <Grid container spacing={3}>
    //                     {s3Url && (
    //                       <Grid item xs={12}>
    //                         <Box>
    //                           <Link
    //                             href={s3Url}
    //                             target="_blank"
    //                             rel="noopener noreferrer"
    //                             sx={{
    //                               textDecoration: "none",
    //                               color: "primary.main",
    //                               display: "flex",
    //                               alignItems: "center",
    //                             }}
    //                           >
    //                             Termite Accomplishment Report PDF Format
    //                             <OpenInNewIcon sx={{ marginLeft: 1, fontSize: "1rem" }} />
    //                           </Link>
    //                         </Box>
    //                       </Grid>
    //                     )}
    //                     {s3UrlPpt && (
    //                       <Grid item xs={12}>
    //                         <Box>
    //                           <Link
    //                             href={s3UrlPpt}
    //                             target="_blank"
    //                             rel="noopener noreferrer"
    //                             sx={{
    //                               textDecoration: "none",
    //                               color: "primary.main",
    //                               display: "flex",
    //                               alignItems: "center",
    //                             }}
    //                           >
    //                             Termite Accomplishment Report PPT Format
    //                             <OpenInNewIcon sx={{ marginLeft: 1, fontSize: "1rem" }} />
    //                           </Link>
    //                         </Box>
    //                       </Grid>
    //                     )}
    //                     <Grid
    //                       item
    //                       xs={12}
    //                       display="flex"
    //                       justifyContent="flex-end"
    //                       alignItems="center"
    //                     >
    //                       <Button
    //                         variant="outlined"
    //                         color="secondary"
    //                         onClick={() => navigate("/manage-accomplishment-reports")}
    //                         sx={{
    //                           fontSize: "0.875rem",
    //                           color: "#FF4C4C",
    //                           borderColor: "#FF4C4C",
    //                         }}
    //                         disabled={isLoading}
    //                       >
    //                         Cancel
    //                       </Button>
    //                       <Button
    //                         variant="contained"
    //                         color="primary"
    //                         onClick={handleSubmit}
    //                         sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
    //                         disabled={isLoading}
    //                       >
    //                         Submit
    //                       </Button>
    //                     </Grid>
    //                   </Grid>
    //                 </CardContent>
    //               </Card>
    //             </Grid>



    //           </Grid>
    //         </Card>
    //       </CardContent>
    //     </Card>
    //   </Box>
    // </ThemeProvider>
  );
}

export default CreateTermiteAccomplishmentReport;